/**
 * Canonical procedure state sequences for tooth chart visuals.
 * Each procedure type maps to an ordered list of display states (PascalCase, ToothChart-compatible).
 */

export const PROCEDURE_STATE_SEQUENCES: Record<string, string[]> = {
  filling: ["Normal", "Filling"],

  rct: ["Normal", "RCT_Open", "Crown_Cutting", "RCT_Done", "RCT_Crown"],

  extraction: ["Normal", "Crown_Cutting", "Missing"],

  crown: ["Normal", "Crown_Cutting", "Crown"],

  implant: ["Missing", "Implant"],
};

/**
 * Returns the state sequence for a procedure type (e.g. "rct", "filling").
 * Falls back to ["Normal"] for unknown types.
 */
export function getProcedureSequence(procedureType: string): string[] {
  const key = (procedureType || "").toLowerCase().trim();
  return PROCEDURE_STATE_SEQUENCES[key] ?? ["Normal"];
}

/**
 * Maps procedure step keys (as logged) to sequence index (0-based).
 * Used to derive visual state from current step when procedure_type is known.
 */
export const STEP_TO_SEQUENCE_INDEX: Record<string, number> = {
  access_opened: 1,
  canal_cleaned: 2,
  obturation_done: 3,
  crown_placed: 4,
};

/**
 * Resolves visual state (PascalCase) from procedure type and step key.
 * Example: getVisualStateFromProcedureStep("rct", "access_opened") → "RCT_Open"
 */
export function getVisualStateFromProcedureStep(
  procedureType: string,
  stepKey: string
): string {
  const seq = getProcedureSequence(procedureType);
  const index = STEP_TO_SEQUENCE_INDEX[stepKey] ?? seq.length - 1;
  const clamped = Math.min(Math.max(index, 0), seq.length - 1);
  return seq[clamped] ?? seq[seq.length - 1] ?? "Normal";
}
